import { computed, MaybeRef, unref } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import { TypeSupportingCustomFieldValues } from '@/gql/graphql'
import { PER_PAGE_MAXIMUM } from '@/utils/constants'
import {
  FieldableFormCustomFieldsQuery,
  FieldableFormInheritedCustomFieldsQuery,
} from '@/components/Fieldable/graphql/queries'

export default function useFieldableCustomFields(
  viewerAccount: MaybeRef<string>,
  accounts: MaybeRef<string[]>,
  appliesTo?: MaybeRef<TypeSupportingCustomFieldValues[]>,
  first: number = PER_PAGE_MAXIMUM,
  includeInherited: boolean = false,
  enabled: MaybeRef<boolean> = true,
) {
  const { result: ownerFieldsResult, loading: ownedFieldsLoading } = useQuery(
    FieldableFormCustomFieldsQuery,
    () => ({
      viewerAccount: unref(viewerAccount),
      accounts: unref(accounts),
      appliesTo: unref(appliesTo),
      first,
    }),
    () => ({
      clientId: 'publicApi',
      enabled: unref(enabled),
    }),
  )
  const { result: inheritedFieldsResult, loading: inheritedFieldsLoading } = useQuery(
    FieldableFormInheritedCustomFieldsQuery,
    () => ({
      viewerAccount: unref(viewerAccount),
      accounts: unref(accounts),
      appliesTo: unref(appliesTo),
      first,
    }),
    () => ({
      clientId: 'publicApi',
      enabled: includeInherited && unref(enabled),
    }),
  )

  const ownedFields = computed(
    () =>
      ownerFieldsResult?.value?.customFields?.data.map((field) => ({
        ...field,
      })) ?? [],
  )

  const inheritedFields = computed(
    () =>
      inheritedFieldsResult?.value?.inheritedCustomFields?.data.map((inheritedField) => ({
        ...inheritedField,
      })) ?? [],
  )

  const fields = computed(() => [...ownedFields.value, ...inheritedFields.value])

  const loading = computed(() => ownedFieldsLoading.value || inheritedFieldsLoading.value)

  return {
    ownedFields,
    ownedFieldsLoading,
    inheritedFields,
    inheritedFieldsLoading,
    fields,
    loading,
  }
}
